import { useRef } from 'react';
import { LuBell, LuLogOut } from 'react-icons/lu';
import { Box } from '@/components/ui/lib/box';
import { Button } from '@/components/ui/lib/button';
import { Flex } from '@/components/ui/lib/flex';
import { Float } from '@/components/ui/lib/float';
import { Link } from '@/components/ui/lib/link';
import { Popover } from '@/components/ui/lib/popover';
import { Portal } from '@/components/ui/lib/portal';
import { Separator } from '@/components/ui/lib/separator';
import { Text } from '@/components/ui/lib/text';
import { Tooltip } from '@/components/ui/lib/tooltip';
import UserAvatar from '@/components/ui/user-avatar';
import { DICTIONARY } from '@/config/dictionary';
import { APP_PATHS } from '@/config/routes/app-paths';
import { countUnread, useNotifications } from '@/hooks/use-notifications';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useAppStore } from '@/stores/app-store';

const UserMenu = () => {
  const { signOut } = useAuth();
  const currentUser = useAppStore((s) => s.currentUser);

  const anchorRef = useRef<HTMLDivElement>(null);

  const { data: notificationsData } = useNotifications(currentUser?.id ?? null);
  const unreadCount = countUnread(notificationsData?.notifications ?? []);

  if (!currentUser) return null;

  return (
    <Popover.Root
      positioning={{ placement: 'top-start', getAnchorElement: () => anchorRef.current }}
    >
      <Tooltip
        content={DICTIONARY.nav.userMenuTooltip}
        positioning={{ placement: 'right' }}
        openDelay={300}
      >
        <Box ref={anchorRef}>
          <Popover.Trigger
            cursor="pointer"
            rounded="full"
            aria-label={DICTIONARY.nav.userMenuTooltip}
            p="0"
            bg="transparent"
            border="none"
          >
            <Box position="relative" display="inline-block">
              <UserAvatar
                displayName={currentUser.displayName}
                className={unreadCount > 0 ? 'ring-2 ring-red-500 ring-offset-2' : undefined}
              />
              {unreadCount > 0 ? (
                <Float placement="top-end" offsetX="-1" offsetY="-1">
                  <Box
                    minW="4"
                    h="4"
                    px="1"
                    rounded="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    className="bg-red-500 text-white"
                    fontSize="9px"
                    fontWeight="700"
                    lineHeight="1"
                  >
                    {unreadCount > 9 ? '9+' : String(unreadCount)}
                  </Box>
                </Float>
              ) : null}
            </Box>
          </Popover.Trigger>
        </Box>
      </Tooltip>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            w="220px"
            p="0"
            overflow="hidden"
            className="bg-surface border-rim shadow-lg rounded-xl"
          >
            {/* User identity */}
            <Flex flexDir="column" gap="0.5" px="4" py="3">
              <Text fontWeight="semibold" fontSize="sm" className="text-base">
                {currentUser.displayName}
              </Text>
              <Text fontSize="xs" className="text-muted">
                {currentUser.email}
              </Text>
            </Flex>

            <Separator className="border-rim" />

            {/* Notifications link */}
            <Box px="2" py="1">
              <Link href={APP_PATHS.overview} _hover={{ textDecoration: 'none' }} display="block">
                <Button
                  variant="ghost"
                  w="full"
                  justifyContent="flex-start"
                  gap="2"
                  fontSize="sm"
                  fontWeight="medium"
                  className="text-base hover:bg-accent-subtle/40 transition-colors"
                >
                  <LuBell className="h-4 w-4 shrink-0" />
                  {DICTIONARY.auth.userMenu.notificationsLabel}
                  {unreadCount > 0 ? (
                    <Box
                      ml="auto"
                      minW="4"
                      h="4"
                      px="1"
                      rounded="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      className="bg-red-500 text-white"
                      fontSize="9px"
                      fontWeight="700"
                      lineHeight="1"
                    >
                      {unreadCount > 9 ? '9+' : String(unreadCount)}
                    </Box>
                  ) : null}
                </Button>
              </Link>
            </Box>

            <Separator className="border-rim" />

            {/* Sign out */}
            <Box px="2" py="2">
              <Button
                variant="ghost"
                w="full"
                justifyContent="flex-start"
                gap="2"
                fontSize="sm"
                fontWeight="medium"
                color="red.600"
                _hover={{ color: 'red.500' }}
                onClick={signOut}
                className="hover:bg-red-500/10 transition-colors"
              >
                <LuLogOut className="h-4 w-4 shrink-0" />
                {DICTIONARY.auth.userMenu.signOut}
              </Button>
            </Box>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

export default UserMenu;
